import { createContext, useContext } from 'react';
import { isAdminImpersonating } from '../services/impersonationSession';

export type AdminRole =
  | 'staff'
  | 'store_manager'
  | 'hq_ops'
  | 'owner'
  | 'area_manager'
  | 'finance'
  | 'unknown';

export type AdminManagedStore = {
  storeId: number;
  storeNameCn: string;
  isPrimary: boolean;
};

export type AdminProfile = {
  fullName: string;
  positionCn: string;
  role: AdminRole;
  storeName: string;
  storeId: number | null;
  isSuperAdmin: boolean;
  /** v0.12.1 新增: actor 真实 employee_id (防自批 UI + audit). */
  employeeId: number | null;
  /** 072 多店挂载 (2026-07-09 admin 切店器): 店长可切换门店集, 非多店店长恒 []. */
  managedStores: AdminManagedStore[];
};

export type AdminScopeStatus =
  'disconnected' | 'checking' | 'connected' | 'unsupported_scope' | 'error';

type AdminScopeContextValue = {
  activeStoreId: number | null;
  profile: AdminProfile | null;
  scopeReady: boolean;
  status: AdminScopeStatus;
  /** 全权限角色 (超管 / owner) 可用顶部切店器切换 activeStoreId; 其它角色恒 false. */
  canSwitchStore: boolean;
  /** 切换查看门店 (null = 返回总览). 仅 canSwitchStore 时生效, 其它角色 no-op. */
  setViewStoreId: (storeId: number | null) => void;
  /**
   * 退出登录 (清 token + 清缓存 → 回登录墙)。
   *
   * 2026-07-11 (稿 frag-adm-e #ae-layout 批注②): 驾驶舱首页把整条 Header display:none,
   * 而"退出"按钮就住在 header 的 AccountConnect 里 —— 老板每次登录的默认落地页上找不到
   * 退出, 得先切去别的页。首页 dock 的账号 chip 要能自己退出, 故把断开逻辑提到壳级。
   */
  logout: () => void;
};

const AdminScopeContext = createContext<AdminScopeContextValue>({
  activeStoreId: null,
  profile: null,
  scopeReady: false,
  status: 'disconnected',
  canSwitchStore: false,
  setViewStoreId: () => undefined,
  logout: () => undefined,
});

export const AdminScopeProvider = AdminScopeContext.Provider;

export function useAdminScope(): AdminScopeContextValue {
  return useContext(AdminScopeContext);
}

export function adminCanUseProcurement(profile: AdminProfile | null): boolean {
  return profile?.isSuperAdmin === true || profile?.role === 'owner' || profile?.role === 'store_manager';
}

export function adminCanUseAiWeekly(profile: AdminProfile | null): boolean {
  return (profile?.isSuperAdmin === true || profile?.role === 'owner') && !isAdminImpersonating();
}

export function adminScopeUnavailableMessage(status: AdminScopeStatus): string {
  if (status === 'disconnected') {
    return '请先微信扫码登录电脑端后查看真实门店数据。';
  }
  if (status === 'error') {
    return '当前电脑端账号不可用，请重新扫码登录。';
  }
  if (status === 'unsupported_scope') {
    return '当前账号没有可操作门店范围，请联系管理员确认权限。';
  }
  return '账号范围校验中，请稍候。';
}

/**
 * 全权限作用域: 超管 / owner / finance 不锁定在自己档案绑定的门店,
 * 默认「全权限·总览」态, 通过顶部切店器主动切入某家店的门店视角 (员工/排班/考勤).
 * 动作类操作 (补录/邀请) 的门店在表单内选, 不依赖切店上下文.
 */
export function adminHasGlobalScope(profile: AdminProfile | null): boolean {
  return (
    profile?.isSuperAdmin === true ||
    profile?.role === 'owner' ||
    profile?.role === 'finance'
  );
}

export function adminCanUseStoreTools(profile: AdminProfile | null): boolean {
  return adminCanManageStoreTools(profile);
}

/** Silent installation-risk observations are visible only to a real,
 * non-impersonated super-admin session. */
export function adminCanUseDeviceRiskLedger(
  profile: AdminProfile | null
): boolean {
  return profile?.isSuperAdmin === true && !isAdminImpersonating();
}

/** Read-only clue pool: owner plus real super-admin, never impersonation. */
export function adminCanUseAnticheatClues(
  profile: AdminProfile | null
): boolean {
  return (
    (profile?.isSuperAdmin === true || profile?.role === 'owner') &&
    !isAdminImpersonating()
  );
}

/** Supported Admin management roles use their real backend capabilities. */
export function adminPeopleOpsReadOnly(_profile: AdminProfile | null): boolean {
  return false;
}

export function adminCanManageStoreTools(
  profile: AdminProfile | null
): boolean {
  if (profile?.isSuperAdmin) {
    return true;
  }
  return (
    profile?.role === 'store_manager' ||
    profile?.role === 'owner' ||
    profile?.role === 'area_manager' ||
    profile?.role === 'finance'
  );
}

export function adminCanUseAttendanceScheduleExport(
  profile: AdminProfile | null
): boolean {
  if (!profile) {
    return false;
  }
  if (profile.isSuperAdmin) {
    return true;
  }
  if (profile.role === 'store_manager') {
    return profile.storeId !== null;
  }
  return (
    profile.role === 'area_manager' ||
    profile.role === 'finance' ||
    profile.role === 'owner'
  );
}

export function adminCanUseInventoryTools(
  profile: AdminProfile | null
): boolean {
  if (profile?.isSuperAdmin) {
    return true;
  }
  return (
    profile?.role === 'finance' ||
    profile?.role === 'owner' ||
    profile?.role === 'area_manager' ||
    adminCanUseStoreTools(profile)
  );
}

export function adminCanUseWeeklyReports(profile: AdminProfile | null): boolean {
  if (profile?.isSuperAdmin) return true;
  return (
    profile?.role === 'store_manager' ||
    profile?.role === 'area_manager' ||
    profile?.role === 'owner'
  );
}

/** Monthly finance workbook: owner / finance / audited super-admin, read-only. */
export function adminCanUseFinanceStatements(
  profile: AdminProfile | null
): boolean {
  if (!profile) return false;
  return (
    profile.isSuperAdmin === true ||
    profile.role === 'owner' ||
    profile.role === 'finance'
  );
}

export function adminFinanceStatementsUnavailableMessage(
  status: AdminScopeStatus,
  profile: AdminProfile | null
): string {
  if (
    status === 'connected' &&
    profile &&
    !adminCanUseFinanceStatements(profile)
  ) {
    return '月度财务报表仅对老板、财务和超级管理者开放。';
  }
  return adminScopeUnavailableMessage(status);
}

export function adminCanUseApprovalInbox(
  profile: AdminProfile | null
): boolean {
  if (!profile) {
    return false;
  }
  if (profile.isSuperAdmin) {
    return true;
  }
  if (profile.role === 'store_manager') {
    return profile.storeId !== null;
  }
  return (
    profile.role === 'owner' ||
    profile.role === 'area_manager' ||
    profile.role === 'finance'
  );
}

export function adminApprovalStoreId(
  profile: AdminProfile | null
): number | null {
  if (!adminCanUseApprovalInbox(profile)) {
    return null;
  }
  if (profile?.isSuperAdmin) {
    return 0;
  }
  if (
    profile?.role === 'owner' ||
    profile?.role === 'area_manager' ||
    profile?.role === 'finance'
  ) {
    return 0;
  }
  return profile?.storeId ?? null;
}

export function adminStoreToolsUnavailableMessage(
  status: AdminScopeStatus,
  profile: AdminProfile | null
): string {
  if (status === 'connected' && profile && !adminCanUseStoreTools(profile)) {
    return '当前账号暂未开放电脑端门店工具，请联系管理员确认权限。';
  }
  // 全权限角色能用门店工具, 走到这里只剩「总览态未选店」一种情况.
  if (status === 'connected' && adminHasGlobalScope(profile)) {
    return '当前处于全权限总览，请先在右上角「查看门店」选择一家门店。';
  }
  return adminScopeUnavailableMessage(status);
}

export function adminAttendanceExportUnavailableMessage(
  status: AdminScopeStatus,
  profile: AdminProfile | null
): string {
  if (
    status === 'connected' &&
    profile?.role === 'store_manager' &&
    profile.storeId === null
  ) {
    return '店长账号还没有绑定门店，暂时不能导出底稿。';
  }
  if (
    status === 'connected' &&
    profile &&
    !adminCanUseAttendanceScheduleExport(profile)
  ) {
    return '当前账号暂未开放考勤排班底稿导出，请使用店长、财务或老板账号。';
  }
  return adminScopeUnavailableMessage(status);
}

export function adminApprovalUnavailableMessage(
  status: AdminScopeStatus,
  profile: AdminProfile | null
): string {
  if (status === 'connected' && profile && !adminCanUseApprovalInbox(profile)) {
    return '当前账号暂未开放审批中心电脑端入口，请联系管理员确认权限。';
  }
  return adminScopeUnavailableMessage(status);
}

export function adminInventoryUnavailableMessage(
  status: AdminScopeStatus,
  profile: AdminProfile | null
): string {
  if (
    status === 'connected' &&
    profile &&
    !adminCanUseInventoryTools(profile)
  ) {
    return '当前账号暂未开放库存电脑端入口，请使用财务、店长或上级管理账号。';
  }
  return adminScopeUnavailableMessage(status);
}

/**
 * 薪酬标准管理 (v0.12.3 主线①) 电脑端可达性。
 *
 * **写集 = finance + owner + super_admin** — 薪酬底数是敏感主数据, 默认由财务维护,
 * 老板账号可达以避免上级账号被业务入口拒绝; hq_ops / area_manager / store_manager / staff
 * 一律不可写。本函数同时控制菜单可见性与「新建 / 编辑」按钮可达。
 *
 * 读集 (list active 供招聘选职级) 后端是全员可读 (RLS select_all USING(true)),
 * 但本管理页是「录入端」(finance / owner 办公场景), 入口按写集收敛, 不暴露给只读角色,
 * 避免只读角色看到「新建」按钮点了被后端 403 的体验断层。
 *
 * 注: 真实权限由后端 require_role("finance", "owner") + super_admin god-mode 兜底,
 * 此函数仅控制菜单可见性与按钮 disabled (前端隐藏只是体验, 后端 RBAC 才是真兜底)。
 */
export function adminCanUseSalaryStandards(
  profile: AdminProfile | null
): boolean {
  if (!profile) {
    return false;
  }
  if (profile.isSuperAdmin) {
    return true;
  }
  return profile.role === 'finance' || profile.role === 'owner';
}

export function adminSalaryStandardsUnavailableMessage(
  status: AdminScopeStatus,
  profile: AdminProfile | null
): string {
  if (
    status === 'connected' &&
    profile &&
    !adminCanUseSalaryStandards(profile)
  ) {
    return '薪酬标准录入仅对财务 / 老板 / 超级管理者开放，请使用有权限账号登录。';
  }
  return adminScopeUnavailableMessage(status);
}

/**
 * 员工入职 (v0.12.1 汇报线) 电脑端可达性。
 *
 * - super_admin: 全权限 (发邀请 / 区域复核 / 老板终审 / 撤销, god-mode 旁路).
 * - owner: 全店发邀请 + 老板终审 + 撤销 (汇报线终审段).
 * - area_manager: 名下门店发邀请 + 区域复核 + 撤销 (汇报线第一段).
 * - store_manager: 本店发邀请 + 撤销自己发起的, 不参与复核.
 * - 其它 role (含 hq_ops / finance / staff / cashier / chef / waiter): 禁入.
 *
 * v0.12.1 改造: hq_ops 不再参与招聘 (Ricky 拍板汇报线 — 店长 → 区域 → 老板).
 *
 * 注: 真实权限由后端 require_role + state 派生兜底, 此函数仅控制菜单可见性与按钮 disabled.
 */
export function adminCanUseEmployeeOnboarding(
  profile: AdminProfile | null
): boolean {
  if (!profile) {
    return false;
  }
  if (profile.isSuperAdmin) {
    return true;
  }
  return (
    profile.role === 'store_manager' ||
    profile.role === 'area_manager' ||
    profile.role === 'owner'
  );
}

export function adminEmployeeOnboardingUnavailableMessage(
  status: AdminScopeStatus,
  profile: AdminProfile | null
): string {
  if (
    status === 'connected' &&
    profile &&
    !adminCanUseEmployeeOnboarding(profile)
  ) {
    return '当前账号暂未开放员工入职邀请功能，请使用店长 / 区域负责人 / 老板账号。';
  }
  return adminScopeUnavailableMessage(status);
}

/**
 * 区域巡检 (QSC) 电脑端可达性。
 *
 * - owner / finance / 超管: 全店只读总览。
 * - area_manager: 名下门店总览 + 复核台 (通过 / 打回)。
 * - store_manager: 本店巡检只读 (现场打分只在小程序, 设计 §14 OQ-7)。
 */
export function adminCanUseInspections(profile: AdminProfile | null): boolean {
  if (!profile) {
    return false;
  }
  if (profile.isSuperAdmin) {
    return true;
  }
  if (profile.role === 'store_manager') {
    return profile.storeId !== null;
  }
  return (
    profile.role === 'owner' ||
    profile.role === 'area_manager' ||
    profile.role === 'finance'
  );
}

/**
 * 卫生自查 (拍照合规) 电脑端可达性。
 *
 * 2026-07-11 Ricky 拍板「区经可见, 老板只读」—— 改前这里只放 owner / 超管, 而后端
 * `PHOTO_COMPLIANCE_REVIEW` 只给 **area_manager**(rbac_matrix.py:267): 唯一有权审核的人
 * 在菜单里根本看不到这个页, 老板看得到却批不了。审核台对唯一能审核的人是隐藏的。
 *
 * 统一巡检入口按冻结角色矩阵直接进入对应任务面：store_manager / area_manager /
 * owner / finance 可进入，super_admin 由上方统一分支放行；hq_ops 不进入本版本巡检工作台。
 * 店长在 Admin 读取本人负责门店的卫生自查任务，真实范围仍由后端收敛。
 *
 * finance 是本次 full-access 新增的 owner-equivalent 读取/导出角色；审核写仍只按后端
 * PHOTO_COMPLIANCE_REVIEW 角色集执行。
 */
export function adminCanUsePhotoCompliance(
  profile: AdminProfile | null
): boolean {
  if (!profile) {
    return false;
  }
  if (profile.isSuperAdmin) {
    return true;
  }
  return (
    profile.role === 'owner' ||
    profile.role === 'area_manager' ||
    profile.role === 'store_manager' ||
    profile.role === 'finance'
  );
}

export type InspectionViewMode = 'overview' | 'mine' | 'store';

export function adminInspectionViewMode(
  profile: AdminProfile | null
): InspectionViewMode | null {
  if (!adminCanUseInspections(profile)) {
    return null;
  }
  if (profile?.isSuperAdmin) {
    return 'overview';
  }
  if (profile?.role === 'owner' || profile?.role === 'finance') {
    return 'overview';
  }
  if (profile?.role === 'area_manager') {
    return 'mine';
  }
  if (profile?.role === 'store_manager') {
    return 'store';
  }
  return null;
}

/** 区域巡检复核权 (通过 / 打回): area_manager 或超管。store_manager / owner 只读。 */
export function adminCanReviewInspections(
  profile: AdminProfile | null
): boolean {
  if (!profile) {
    return false;
  }
  return profile.isSuperAdmin || profile.role === 'area_manager';
}

export function adminInspectionUnavailableMessage(
  status: AdminScopeStatus,
  profile: AdminProfile | null
): string {
  if (status === 'connected' && profile && !adminCanUseInspections(profile)) {
    return '当前账号暂未开放区域巡检电脑端入口，请使用区域负责人、老板或店长账号。';
  }
  return adminScopeUnavailableMessage(status);
}

/**
 * 驾驶舱首页 (owner-home 看板) 电脑端可达性。
 *
 * 2026-07-09 Ricky "店长/区经/财务 admin 首页对齐老板端": 四角色首页统一渲染驾驶舱,
 * 数据范围由后端缩权 (owner/finance=全直营 / area=名下店聚合 / 店长=聚合锁本店+
 * 对比榜全直营), 块级差异走 adminOwnerDashboardShowsBrand / adminOwnerDashboardCanDrill。
 * hq_ops / staff 仍走基础工作台。
 *
 * 注: 真实可见范围由后端 owner-home service visible_store_ids 兜底,
 * 此函数仅控制 Admin 首页是否渲染看板, 不做权限决策。
 */
export function adminCanUseOwnerHome(profile: AdminProfile | null): boolean {
  if (!profile) {
    return false;
  }
  if (profile.isSuperAdmin) {
    return true;
  }
  return (
    profile.role === 'owner' ||
    profile.role === 'area_manager' ||
    profile.role === 'store_manager' ||
    profile.role === 'finance'
  );
}

/**
 * 驾驶舱品牌聚合块 (7月总目标达成/品牌小计/品牌营收占比/品牌筛选 chip) 可见性:
 * 店长没必要看品牌聚合 (Ricky 2026-07-09 拍板), 区经/财务/owner/超管可见。
 */
export function adminOwnerDashboardShowsBrand(
  profile: AdminProfile | null
): boolean {
  if (!profile) {
    return false;
  }
  if (profile.isSuperAdmin) {
    return true;
  }
  return (
    profile.role === 'owner' ||
    profile.role === 'area_manager' ||
    profile.role === 'finance'
  );
}

/**
 * 驾驶舱店卡下钻与 manager-home 的真实角色矩阵保持一致。
 */
export function adminOwnerDashboardCanDrill(
  profile: AdminProfile | null
): boolean {
  if (!profile) {
    return false;
  }
  if (profile.isSuperAdmin) {
    return true;
  }
  return (
    profile.role === 'owner' ||
    profile.role === 'area_manager' ||
    profile.role === 'finance'
  );
}

/**
 * 门店档案 (2026-06-04 主数据基座) 电脑端可达性。
 *
 * 设计依据: docs/design/2026-06-04-store-profile-and-performance-tech-design.md §2.4 + §4。
 *
 * 维护集 = store_manager (RLS 限本店) + area_manager + finance + owner + super_admin。
 * 店长维护门店经营属性 (面积/桌位/类型/楼层/编制); 房租 / 经营性质等敏感字段后续接
 * 审批中台 (§2.2), v1 直写。其它角色 (hq_ops / staff) 不可达。
 *
 * 注: 真实可见 / 可写范围由后端 require_role + RLS (店长见本店 / 老板见全部) 兜底,
 * 此函数仅控制菜单可见性与页面 scope gate (前端隐藏只是体验, 后端 RBAC 才是真兜底)。
 */
export function adminCanUseStoreProfile(profile: AdminProfile | null): boolean {
  if (!profile) {
    return false;
  }
  if (profile.isSuperAdmin) {
    return true;
  }
  if (profile.role === 'store_manager') {
    return profile.storeId !== null;
  }
  return (
    profile.role === 'area_manager' ||
    profile.role === 'finance' ||
    profile.role === 'owner'
  );
}

/** Store master/status writes are intentionally narrower than profile maintenance. */
export function adminCanManageStoreConfig(
  profile: AdminProfile | null
): boolean {
  return profile?.isSuperAdmin === true || profile?.role === 'owner';
}

export function adminStoreProfileUnavailableMessage(
  status: AdminScopeStatus,
  profile: AdminProfile | null
): string {
  if (status === 'connected' && profile && !adminCanUseStoreProfile(profile)) {
    return '门店配置仅对店长 / 区域 / 财务 / 老板 / 超级管理者开放，请使用有权限账号登录。';
  }
  return adminScopeUnavailableMessage(status);
}

/**
 * 绩效管理 (2026-06-04 月度目标 + 实时预估利润) 电脑端可达性。
 *
 * 设计依据: docs/design/2026-06-04-store-profile-and-performance-tech-design.md §3.4 + §4。
 *
 * 可达集 = owner + area_manager + finance + super_admin (与设计 roleAware: owner/区域/财务 对齐)。
 * 利润 / 毛利为实时「预估」(实时营收 × 成本模型), 月末财务结账后 true-up; 店长 / staff 不在
 * 本电脑端绩效总览入口 (店长单店经营概览走 owner/manager-home 线, 非本页)。
 *
 * 注: 真实可见门店范围由后端 visible_store_ids (RLS) 兜底, 此函数仅控制菜单可见性与
 * 页面 scope gate, 不做权限决策。
 */
export function adminCanUsePerformance(profile: AdminProfile | null): boolean {
  if (!profile) {
    return false;
  }
  if (profile.isSuperAdmin) {
    return true;
  }
  return (
    profile.role === 'owner' ||
    profile.role === 'area_manager' ||
    profile.role === 'finance'
  );
}

export function adminPerformanceUnavailableMessage(
  status: AdminScopeStatus,
  profile: AdminProfile | null
): string {
  if (status === 'connected' && profile && !adminCanUsePerformance(profile)) {
    return '绩效管理仅对老板 / 区域 / 财务 / 超级管理者开放，请使用有权限账号登录。';
  }
  return adminScopeUnavailableMessage(status);
}

/**
 * 品类维护 (老板看板③消费点单 配套后台) 电脑端可达性。
 *
 * 后端契约: 读 (GET dish-categories) 任意已认证 actor 可看 (门店只读); 写
 * (PUT dish-categories/{brand}/{dish_code}) **finance / owner / super_admin**。
 *
 * 本管理页是「手动维护菜品↔品类归属」的录入端, 入口按**写集**
 * 收敛 = finance + owner + super_admin。其它角色菜单隐藏, 不暴露"点了被后端 403"的入口。
 *
 * 注: 真实写权限由后端 service 层 RBAC + super_admin god-mode 兜底, 此函数仅控制菜单
 * 可见性与页面 scope gate (前端隐藏只是体验, 后端 RBAC 才是真兜底)。
 */
export function adminCanUseDishCategory(profile: AdminProfile | null): boolean {
  if (!profile) {
    return false;
  }
  if (profile.isSuperAdmin) {
    return true;
  }
  return profile.role === 'owner' || profile.role === 'finance';
}

export function adminDishCategoryUnavailableMessage(
  status: AdminScopeStatus,
  profile: AdminProfile | null
): string {
  if (status === 'connected' && profile && !adminCanUseDishCategory(profile)) {
    return '品类维护仅对财务 / 老板 / 超级管理者开放，请使用有权限账号登录。';
  }
  return adminScopeUnavailableMessage(status);
}

export function adminRoleFromPosition(position: unknown): AdminRole {
  if (
    position === 'staff' ||
    position === 'store_manager' ||
    position === 'hq_ops' ||
    position === 'owner' ||
    position === 'area_manager' ||
    position === 'finance'
  ) {
    return position;
  }
  return 'unknown';
}
